import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import Field from '@atlaskit/form/field';
import type { ValueType } from '@atlaskit/select/types';
import Select from '@atlaskit/select/select';
import { AnalyticsContext } from '@atlaskit/analytics-next';

import type { AvailableSite } from '@atlassian/gic-anywhere/utils';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages.ts';
import {
	useAvailableSitesActions,
	useAvailableSitesState,
	useIsConfigureLoading,
} from '../../providers/BulkConfigureContextProvider.tsx';

interface AvailableSiteOption {
	label: string;
	value: AvailableSite;
}

export const mapSiteToOption = (site: AvailableSite): AvailableSiteOption => ({
	label: site.displayName,
	value: site,
});

// TODO: Remove optional props once we cleanup jira_ai_decouple_issue_create_fields FF
interface SiteSelectorProps {
	options: AvailableSite[];
	value?: AvailableSite | null;
	onChange?: (site: AvailableSite | null) => void;
	isLoading?: boolean;
	isDisabled?: boolean;
	isRequired?: boolean;
	label?: string;
}

// TODO: Remove Partial type once we cleanup jira_ai_decouple_issue_create_fields FF
export const SiteSelector: React.FC<Partial<SiteSelectorProps>> = (props) => {
	const { formatMessage } = useIntl();
	// Old Sweet State logic
	const { selectedAvailableSite, optionsAvailableSites } = useAvailableSitesState();
	const { setSelectedAvailableSite } = useAvailableSitesActions();
	const { isLoading: isConfigureLoading } = useIsConfigureLoading();

	const optionsSweetState = useMemo(() => {
		return optionsAvailableSites?.map(mapSiteToOption);
	}, [optionsAvailableSites]);

	const onChangeSweetState = useCallback(
		(selectedOptionSweetState: ValueType<AvailableSiteOption>) => {
			if (selectedOptionSweetState && selectedOptionSweetState.value !== selectedAvailableSite) {
				void setSelectedAvailableSite(selectedOptionSweetState.value);
			}
		},
		[selectedAvailableSite, setSelectedAvailableSite],
	);

	// New decoupled logic (props/local state)
	const {
		options = [],
		value: controlledValue,
		onChange,
		isLoading = false,
		isDisabled = false,
		label,
	} = props;

	const [localValue, setLocalValue] = useState<AvailableSite | null>(controlledValue ?? null);
	const mappedOptions = useMemo(() => options?.map(mapSiteToOption), [options]);
	const selectedValue = localValue ? mapSiteToOption(localValue) : null;

	// Keep local state in sync with controlled value
	useEffect(() => {
		if (!fg('jira_ai_decouple_issue_create_fields')) {
			return;
		}

		if (controlledValue !== undefined) {
			setLocalValue(controlledValue);
		}
	}, [controlledValue, setLocalValue]);

	if (!fg('jira_ai_decouple_issue_create_fields')) {
		return (
			<Field<ValueType<AvailableSiteOption>>
				name="sites"
				label={formatMessage(messages.bulkConfigureFormSite)}
				testId="work-create-site-field"
			>
				{({ fieldProps: { id, ...rest } }) => (
					<Fragment>
						<AnalyticsContext data={{ attributes: { fieldName: 'workCreateFormSiteSelector' } }}>
							<Select<AvailableSiteOption>
								inputId={id}
								{...rest}
								value={selectedAvailableSite ? mapSiteToOption(selectedAvailableSite) : null}
								options={optionsSweetState}
								onChange={onChangeSweetState}
								isLoading={isConfigureLoading}
								isDisabled={isConfigureLoading}
							/>
						</AnalyticsContext>
					</Fragment>
				)}
			</Field>
		);
	}

	const handleChange = (selectedOption: ValueType<AvailableSiteOption>) => {
		if (!selectedOption || !selectedOption.value) {
			return;
		}

		if (selectedOption.value === localValue) {
			return;
		}

		const newValue = selectedOption ? selectedOption.value : null;
		setLocalValue(newValue);
		onChange?.(newValue);
	};

	return (
		<Field<ValueType<AvailableSiteOption>>
			name="sites"
			// TODO: Remove formatMessage(messages.bulkConfigureFormSite) once we cleanup jira_ai_decouple_issue_create_fields FF
			label={label ?? formatMessage(messages.bulkConfigureFormSite)}
			testId="work-create-site-field"
			isRequired={props.isRequired ?? false}
		>
			{({ fieldProps: { id, ...rest } }) => (
				<Fragment>
					<AnalyticsContext data={{ attributes: { fieldName: 'workCreateFormSiteSelector' } }}>
						<Select<AvailableSiteOption>
							inputId={id}
							{...rest}
							value={selectedValue}
							options={mappedOptions}
							onChange={handleChange}
							isLoading={isLoading}
							isDisabled={isDisabled || isLoading}
						/>
					</AnalyticsContext>
				</Fragment>
			)}
		</Field>
	);
};
